import { SelectChangeEvent } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import {
	editFormSchema,
	NO,
	ROUTES,
	VALUES_STRUCTURE_TYPES,
	YES_NO,
} from '../../constants/TeamRegister';
import api from '../../services/apiSgft';
import { Button } from '../atoms/Button';
import ControlledSimpleSelect, {
	Values,
} from '../atoms/ControlledSimpleSelect';
import Title from '../atoms/Title';
import UncontrolledInputText from '../atoms/UncontrolledInputText';
import Subtitle from '../molecules/Subtitle';

type methodsSchema = z.infer<typeof editFormSchema>;

interface Props {
	type: 'create' | 'edit';
	scales?: { value: string; label: string }[];
	teamsLength?: { value: number; label: string }[];
	getScalesAndTeamsLength?: (e?: SelectChangeEvent<unknown>) => void;
	onAbort: () => void;
	initialBoard?: number;
	initialManagement?: number;
	initialCoordination?: number;
}

export const RegisterAndEditTeam = ({
	type,
	scales,
	teamsLength,
	getScalesAndTeamsLength,
	onAbort,
	initialBoard,
	initialManagement,
	initialCoordination,
}: Props) => {
	const methods = useFormContext<methodsSchema>();
	const sectorWatch = methods.watch('sector');
	const onNoticeWatch = methods.watch('onNotice');
	const companyWatch = methods.watch('company');
	const boardWatch = methods.watch('board');
	const managementWatch = methods.watch('management');
	const [values, setValues] = useState<{
		company: Values[];
		sector: Values[];
		workstation: Values[];
	}>();
	const [companyBoardManagementMap, setCompanyBoardManagementMap] = useState<
		Record<
			number,
			{
				boards: Array<{ id: number; name: string }>;
				managementsByBoard: Record<
					number,
					Array<{ id: number; name: string }>
				>;
				coordinationsByManagement: Record<
					number,
					Array<{ id: number; name: string }>
				>;
			}
		>
	>({});
	const [initialValuesSet, setInitialValuesSet] = useState(false);

	useEffect(() => {
		async function fetchCompaniesBoardsAndManagements() {
			try {
				const response = await api.get(`team-structure/company-mapping`);
				const companiesData = response.data?.companies || response.data || {};
				
				const mapping: Record<
					number,
					{
						boards: Array<{ id: number; name: string }>;
						managementsByBoard: Record<
							number,
							Array<{ id: number; name: string }>
						>;
						coordinationsByManagement: Record<
							number,
							Array<{ id: number; name: string }>
						>;
					}
				> = {};

				for (const companyData of companiesData) {
					const companyId = companyData.id;
					if (isNaN(companyId)) continue;

					const company = companyData as any;
					const boards = company.boards || [];

					mapping[companyId] = {
						boards: boards.map((board: any) => ({
							id: board.id,
							name: board.name,
						})),
						managementsByBoard: {},
						coordinationsByManagement: {},
					};

					for (const board of boards) {
						const managements = board.managements || [];
						
						mapping[companyId].managementsByBoard[board.id] = managements.map(
							(management: any) => ({
								id: management.id,
								name: management.name,
							}),
						);

						for (const management of managements) {
							const coordinations = management.coordinations || [];
							
							mapping[companyId].coordinationsByManagement[management.id] = 
								coordinations.map((coordination: any) => ({
									id: coordination.id,
									name: coordination.name,
								}));
						}
					}
				}

				setCompanyBoardManagementMap(mapping);
			} catch (error) {
				console.error(
					'Erro ao carregar mapeamento de empresas, diretorias, gerências e coordenações:',
					error,
				);
				toast.error(
					'Erro ao carregar o mapeamento de empresas, diretorias, gerências e coordenações',
					{
						theme: 'colored',
						toastId: 'error-mapping',
					},
				);
			}
		}

		fetchCompaniesBoardsAndManagements();
	}, []);

	const boards = useMemo(() => {
		const hasValidCompany = companyWatch && companyWatch !== -1;
		if (!hasValidCompany) return [];

		const companyData = companyBoardManagementMap[companyWatch];
		if (!companyData) return [];

		return companyData.boards.map((board) => ({
			label: board.name,
			value: board.id,
		}));
	}, [companyWatch, companyBoardManagementMap]);

	const managements = useMemo(() => {
		const hasValidBoard = boardWatch && boardWatch !== -1;
		const hasValidCompany = companyWatch && companyWatch !== -1;
		
		if (!hasValidBoard || !hasValidCompany) return [];

		const companyData = companyBoardManagementMap[companyWatch];
		if (!companyData) return [];

		const managementsData = companyData.managementsByBoard[boardWatch];
		if (!managementsData) return [];

		return managementsData.map((management) => ({
			label: management.name,
			value: management.id,
		}));
	}, [boardWatch, companyWatch, companyBoardManagementMap]);

	const coordinations = useMemo(() => {
		const hasValidManagement = managementWatch && managementWatch !== -1;
		const hasValidCompany = companyWatch && companyWatch !== -1;
		
		if (!hasValidManagement || !hasValidCompany) return [];

		const companyData = companyBoardManagementMap[companyWatch];
		if (!companyData) return [];

		const coordinationsData = companyData.coordinationsByManagement[managementWatch];
		if (!coordinationsData) return [];

		return coordinationsData.map((coordination) => ({
			label: coordination.name,
			value: coordination.id,
		}));
	}, [managementWatch, companyWatch, companyBoardManagementMap]);

	useEffect(() => {
		const isMappingReady = Object.keys(companyBoardManagementMap).length > 0;
		const hasValidCompany = companyWatch && companyWatch !== -1;

		if (!hasValidCompany || !isMappingReady) return;

		const companyData = companyBoardManagementMap[companyWatch];
		if (!companyData) return;

		const currentBoard = methods.getValues('board');
		const currentManagement = methods.getValues('management');
		const currentCoordination = methods.getValues('coordination');

		const isValidBoard = currentBoard && currentBoard !== -1;
		if (isValidBoard) {
			const boardExists = companyData.boards.some(
				(board) => board.id === currentBoard,
			);
			if (!boardExists) {
				methods.setValue('board', undefined as any);
				methods.setValue('management', undefined as any);
				methods.setValue('coordination', undefined as any);
				return;
			}
		}

		const isValidManagement = currentManagement && isValidBoard;
		if (!isValidManagement) {
			if (currentCoordination) {
				methods.setValue('coordination', undefined as any);
			}
			return;
		}

		const managementsData = companyData.managementsByBoard[currentBoard];
		if (!managementsData) {
			if (currentCoordination) {
				methods.setValue('coordination', undefined as any);
			}
			return;
		}

		const managementExists = managementsData.some(
			(management) => management.id === currentManagement,
		);
		if (!managementExists) {
			methods.setValue('management', undefined as any);
			methods.setValue('coordination', undefined as any);
			return;
		}

		const isValidCoordination = currentCoordination && currentCoordination !== -1;
		if (!isValidCoordination) return;

		const coordinationsData = companyData.coordinationsByManagement[currentManagement];
		if (!coordinationsData) {
			methods.setValue('coordination', undefined as any);
			return;
		}

		const coordinationExists = coordinationsData.some(
			(coordination) => coordination.id === currentCoordination,
		);
		if (!coordinationExists) {
			methods.setValue('coordination', undefined as any);
		}
	}, [companyWatch, boardWatch, managementWatch, companyBoardManagementMap, methods]);

	const prevManagementRef = React.useRef<number | undefined>(undefined);

	useEffect(() => {
		if (type === 'edit' && !initialValuesSet) {
			prevManagementRef.current = managementWatch;
			return;
		}
		if (!managementWatch) {
			prevManagementRef.current = managementWatch;
			return;
		}
		
		if (prevManagementRef.current !== undefined && prevManagementRef.current !== managementWatch) {
			const currentCoordination = methods.getValues('coordination');
			if (currentCoordination) {
				methods.setValue('coordination', undefined as any);
			}
		}
		
		prevManagementRef.current = managementWatch;
	}, [managementWatch, type, initialValuesSet, methods]);

	useEffect(() => {
		const isEditMode = type === 'edit';
		const isMappingReady = Object.keys(companyBoardManagementMap).length > 0;
		const hasValidCompany = companyWatch && companyWatch !== -1;
		const shouldSetInitials = isEditMode && !initialValuesSet && hasValidCompany && isMappingReady;

		if (!shouldSetInitials) return;

		const companyData = companyBoardManagementMap[companyWatch];
		if (!companyData) return;

		if (initialBoard) {
			const boardExists = companyData.boards.some(
				(board) => board.id === initialBoard,
			);
			if (boardExists) {
				const currentBoard = methods.getValues('board');
				if (!currentBoard) {
					methods.setValue('board', initialBoard);
				}
			}
		}

		if (!initialManagement) {
			setInitialValuesSet(true);
			return;
		}

		const boardToUse = methods.getValues('board') || initialBoard;
		if (!boardToUse) {
			setInitialValuesSet(true);
			return;
		}

		const managementsData = companyData.managementsByBoard[boardToUse];
		if (!managementsData) {
			setInitialValuesSet(true);
			return;
		}

		const managementExists = managementsData.some(
			(mgmt) => mgmt.id === initialManagement,
		);
		if (managementExists) {
			const currentManagement = methods.getValues('management');
			if (!currentManagement) {
				prevManagementRef.current = initialManagement;
				methods.setValue('management', initialManagement);
			}
		}

		if (!initialCoordination) {
			setInitialValuesSet(true);
			return;
		}

		const managementToUse = initialManagement;
		if (!managementToUse) {
			setInitialValuesSet(true);
			return;
		}

		const coordinationsData = companyData.coordinationsByManagement[managementToUse];
		if (!coordinationsData) {
			setInitialValuesSet(true);
			return;
		}

		const coordinationExists = coordinationsData.some(
			(coord) => coord.id === initialCoordination,
		);
		if (coordinationExists) {
			const currentCoordination = methods.getValues('coordination');
			if (currentCoordination !== initialCoordination) {
				prevManagementRef.current = initialManagement;
				methods.setValue('coordination', initialCoordination, { shouldDirty: false });
			}
		}

		setInitialValuesSet(true);
	}, [
		type,
		initialBoard,
		initialManagement,
		initialCoordination,
		companyWatch,
		companyBoardManagementMap,
		initialValuesSet,
		methods,
	]);

	const onNoticeValues = useMemo(() => {
		return onNoticeWatch === 'NÃO' ? NO : YES_NO;
	}, [onNoticeWatch]);

	useEffect(() => {
		async function fetchValues() {
			try {
				const fetchPromises = ROUTES.map(async (el) => {
					const response = await api.get(`${el.route}/all`);
					const data = response.data.map((it: { name: any; id: any }) => ({
						label: it.name,
						value: it.id,
					}));
					
					return { key: el.route, value: data };
				});

				const resolvedValues = await Promise.all(fetchPromises);
				const valuesMap = new Map(
					resolvedValues.map((item) => [item.key, item.value]),
				);

				setValues({
					company: valuesMap.get('company') || [],
					sector: valuesMap.get('team-structure/sector') || [],
					workstation: valuesMap.get('work-station') || [],
				});
			} catch (e) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: 'error',
				});
			}
		}

		fetchValues();
	}, []);

	return (
		<div className="mx-auto mt-1 flex w-full max-w-[110rem] justify-center">
			<div className="w-full flex-col  ">
				<Title
					title={`${type === 'create' ? 'Cadastro de Estrutura de Turma' : 'Edição de Estrutura de Turma'}`}
				/>
				<div className="flex min-h-[75vh] flex-col justify-between rounded-[15px] bg-white px-4 pb-2 pt-8">
					<div>
						<div className="center mb-7 flex w-full justify-start">
							<UncontrolledInputText
								className="mr-5"
								title={'Nome da Estrutura'}
								required
								width="18rem"
								{...methods.register('name')}
								fieldError={methods.formState.errors.name}
								errorMessage="O nome é obrigatório*"
							/>
						</div>
						<Subtitle subtitle="Informações Gerais" />
						<div className="mb-7 flex w-full flex-wrap gap-2 space-y-px">
							<ControlledSimpleSelect
								className="mr-5"
								title={'Empresa'}
								required
								name={'company'}
								width="18rem"
								values={values?.company}
								control={methods.control}
								fieldError={methods.formState.errors.company}
								errorMessage="A empresa é obrigatória*"
							/>
							<ControlledSimpleSelect
								className="mr-5"
								title={'Diretoria'}
								required
								name={'board'}
								width="18rem"
								values={boards}
								disabled={boards?.length === 0}
								control={methods.control}
								fieldError={methods.formState.errors.board}
								errorMessage="A diretoria é obrigatória*"
							/>
							<ControlledSimpleSelect
								className="mr-5"
								title={'Gerência'}
								required
								name={'management'}
								width="18rem"
								values={managements}
								disabled={managements?.length === 0}
								control={methods.control}
								fieldError={methods.formState.errors.management}
								errorMessage="A gerência é obrigatória*"
							/>
							<ControlledSimpleSelect
								className="mr-5"
								title={'Coordenação'}
								required
								control={methods.control}
								values={coordinations}
								disabled={coordinations?.length === 0}
								name={'coordination'}
								width="18rem"
								fieldError={methods.formState.errors.coordination}
								errorMessage="A coordenação é obrigatória*"
							/>
							<div>
								<ControlledSimpleSelect
									className="mr-5"
									title={'Setor'}
									control={methods.control}
									required
									values={values?.sector}
									name={'sector'}
									width="18rem"
									fieldError={methods.formState.errors.sector}
									errorMessage="O setor é obrigatório*"
								/>
								{sectorWatch === '-1' && (
									<UncontrolledInputText
										title={'Digite um novo setor'}
										width="18rem"
										{...methods.register('otherSector')}
									/>
								)}
							</div>
							<ControlledSimpleSelect
								title={'Local de Trabalho'}
								control={methods.control}
								required
								values={values?.workstation}
								name={'workstation'}
								width="18rem"
								multiple
								fieldError={methods.formState.errors.workstation}
								errorMessage=" O local de trabalho é obrigatório*"
							/>
						</div>
						{type === 'create' && (
							<div>
								<Subtitle subtitle="Regime e Especificidades" />
								<div className="mb-5 flex w-full flex-wrap justify-between">
									<ControlledSimpleSelect
										className="mr-5"
										title="Tipo da Estrutura"
										required
										width="18rem"
										values={VALUES_STRUCTURE_TYPES}
										control={methods.control}
										name={'structureType'}
										fieldError={methods.formState.errors.structureType}
										errorMessage="O tipo da estrutura é obrigatório*"
										onChange={(e) => {
											getScalesAndTeamsLength && getScalesAndTeamsLength(e);
										}}
									/>
									<ControlledSimpleSelect
										className="mr-5"
										title="Escala"
										required
										width="18rem"
										values={scales}
										disabled={scales?.length === 0}
										control={methods.control}
										name={'scale'}
										fieldError={methods.formState.errors.scale}
										errorMessage="A escala é obrigatória*"
									/>
									<ControlledSimpleSelect
										disabled={teamsLength?.length === 0}
										title="Número de turmas"
										required
										values={teamsLength}
										control={methods.control}
										name={'teamsLength'}
										width="18rem"
										fieldError={methods.formState.errors.teamsLength}
										errorMessage=" O número de turmas é obrigatório*"
									/>
									<div className="w-[18rem]" />
								</div>
							</div>
						)}

						<Subtitle subtitle="Sobreaviso" />
						<div className="mb-[1rem] flex w-full flex-wrap justify-between">
							<ControlledSimpleSelect
								className="mr-5"
								title="Sobreaviso"
								required
								width="18rem"
								values={YES_NO}
								control={methods.control}
								name={'onNotice'}
								fieldError={methods.formState.errors.onNotice}
								errorMessage="O compo sobreaviso é obrigatório*"
							/>
							<ControlledSimpleSelect
								className="mr-5"
								title="Sobreaviso obrigatório"
								required
								width="18rem"
								values={onNoticeValues}
								control={methods.control}
								name={'mandatoryOnNotice'}
								fieldError={methods.formState.errors.mandatoryOnNotice}
								errorMessage="O campo sobreaviso obrigatório é obrigatório*"
							/>
							<ControlledSimpleSelect
								title="Confinamento"
								values={onNoticeValues}
								required
								control={methods.control}
								name={'confinement'}
								width="18rem"
								fieldError={methods.formState.errors.confinement}
								errorMessage="O campo confinamento é obrigatório*"
							/>
							<div className="w-[18rem]" />
						</div>
					</div>
					<div className="mb-1 mt-5 flex justify-end">
						{type === 'create' ? (
							<div className="flex w-full justify-between">
								<Button
									type="button"
									className="mr-5 min-w-36 border-2 border-[#193CB9] bg-white text-[#193CB9] hover:bg-[#e9eaf1]"
									onClick={() => onAbort()}
								>
									Cancelar
								</Button>
								<Button type="submit" className="mb-5 min-w-36">
									Prosseguir
								</Button>
							</div>
						) : (
							<div className="flex w-full justify-between">
								<Button
									type="button"
									className="min-w-36 border-2 border-[#193CB9] bg-white text-[#193CB9] hover:bg-[#e9eaf1]"
									onClick={() => onAbort()}
								>
									Cancelar
								</Button>
								<Button
									type="submit"
									disabled={!methods.formState.isDirty}
									className="mb-5 min-w-36"
								>
									Salvar Alterações
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
